import { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";
import { useLanguage } from "./LanguageContext";

const START_AT = 0; // play the uploaded track from the beginning
const GAIN = 1; // uploaded track is already at a good level — no boost needed

export default function MusicToggle() {
  const { t } = useLanguage();
  const audioRef = useRef(null);
  const ctxRef = useRef(null);
  const userPausedRef = useRef(false);
  const [playing, setPlaying] = useState(false);

  // Build a Web Audio graph once so we can amplify the quiet source (gain > 1).
  const ensureGraph = () => {
    const audio = audioRef.current;
    if (!audio || ctxRef.current || GAIN === 1) return;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    try {
      const ctx = new AC();
      const source = ctx.createMediaElementSource(audio);
      const gain = ctx.createGain();
      gain.gain.value = GAIN;
      source.connect(gain).connect(ctx.destination);
      ctxRef.current = ctx;
    } catch {
      /* graph unavailable — fall back to plain element volume */
    }
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = 0.6;
    audio.loop = true;

    const startPlayback = () => {
      if (userPausedRef.current || !audioRef.current) return;
      ensureGraph();
      if (ctxRef.current && ctxRef.current.state === "suspended") {
        ctxRef.current.resume().catch(() => {});
      }
      const play = () => audio.play().then(() => setPlaying(true)).catch(() => {});
      const seekThenPlay = () => {
        try {
          if (audio.currentTime < START_AT) audio.currentTime = START_AT;
        } catch {
          /* ignore */
        }
        play();
      };
      if (audio.readyState >= 2) seekThenPlay();
      else {
        audio.addEventListener("canplay", seekThenPlay, { once: true });
        play();
      }
    };

    const onEnded = () => {
      try {
        audio.currentTime = START_AT;
      } catch {
        /* ignore */
      }
      audio.play().catch(() => {});
    };
    audio.addEventListener("ended", onEnded);

    const gestureEvents = ["pointerdown", "keydown", "touchstart"];
    const onGesture = () => {
      startPlayback();
      if (!userPausedRef.current) removeGestures();
    };
    const removeGestures = () =>
      gestureEvents.forEach((e) => window.removeEventListener(e, onGesture));

    startPlayback(); // try immediate autoplay
    gestureEvents.forEach((e) =>
      window.addEventListener(e, onGesture, { passive: true }),
    );

    return () => {
      removeGestures();
      audio.removeEventListener("ended", onEnded);
      audio.pause();
    };
  }, []);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      userPausedRef.current = true;
      audio.pause();
      setPlaying(false);
    } else {
      userPausedRef.current = false;
      ensureGraph();
      if (ctxRef.current && ctxRef.current.state === "suspended") {
        ctxRef.current.resume().catch(() => {});
      }
      if (audio.readyState >= 2 && audio.currentTime < START_AT) {
        try {
          audio.currentTime = START_AT;
        } catch {
          /* ignore */
        }
      }
      audio.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  };

  return (
    <>
      <audio ref={audioRef} src="/music/wedding-song.mp3" preload="auto" />
      <motion.button
        data-testid="music-toggle"
        onClick={toggle}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 3.4 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        aria-label={playing ? t.music.pause : t.music.play}
        className="fixed bottom-6 left-6 z-50 flex items-center gap-2.5 rounded-full border border-gold/40 bg-cream/80 backdrop-blur-md px-4 py-2.5 shadow-[0_8px_32px_rgba(15,122,147,0.12)]"
      >
        <span className="relative grid h-6 w-6 place-items-center text-turq">
          {playing ? (
            <Volume2 className="h-4 w-4" strokeWidth={1.8} />
          ) : (
            <VolumeX className="h-4 w-4" strokeWidth={1.8} />
          )}
        </span>
        <span className="flex items-end gap-0.5 h-4">
          {[0, 1, 2].map((i) => (
            <motion.span
              key={i}
              className="w-0.5 rounded-full bg-gold"
              animate={
                playing ? { height: ["4px", "14px", "6px"] } : { height: "4px" }
              }
              transition={
                playing
                  ? {
                      duration: 0.7,
                      repeat: Infinity,
                      delay: i * 0.15,
                      repeatType: "reverse",
                    }
                  : { duration: 0.2 }
              }
            />
          ))}
        </span>
      </motion.button>
    </>
  );
}
